import React, { FC } from 'react'

type StarSize = 'md' | 'lg' | 'xl'
type StarColor = 'green' | 'white'

interface StarIconProps {
  size?: StarSize
  color?: StarColor
}

interface EvaluationStarIconProps {
  evaluation: number | null
  size?: StarSize
  color?: StarColor
}

const getSizeProps = (size: StarSize = 'md') => {
  switch (size) {
    case 'md':
      return { width: '15.364', height: '15.577' }
    case 'lg':
      return { width: '25.364', height: '25.577' }
    case 'xl':
      return { width: '35.17', height: '33.66' }
  }
}

const getColorValue = (color: StarColor = 'green') => {
  return color === 'white' ? '#FFFFFF' : 'var(--color-main-green)'
}

export const StarIcon: FC<StarIconProps> = ({ size = 'md', color = 'green' }) => {
  const { width, height } = getSizeProps(size)
  const fill = getColorValue(color)

  return (
    <div className="min-w-[16px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 18.364 17.577"
      >
        <path
          id="FontAwsome_star_"
          data-name="FontAwsome (star)"
          d="M28.7.6,26.455,5.144l-5.015.731a1.1,1.1,0,0,0-.608,1.874l3.628,3.536L23.6,16.279A1.1,1.1,0,0,0,25.2,17.436l4.486-2.358,4.486,2.358a1.1,1.1,0,0,0,1.593-1.157L34.9,11.284,38.53,7.749a1.1,1.1,0,0,0-.608-1.874l-5.015-.731L30.666.6A1.1,1.1,0,0,0,28.7.6Z"
          transform="translate(-20.5 0.013)"
          fill={fill}
        />
      </svg>
    </div>
  )
}

export const EmptyStarIcon: FC<StarIconProps> = ({ size = 'md', color = 'green' }) => {
  const { width, height } = getSizeProps(size)
  const fill = getColorValue(color)

  return (
    <div className="min-w-[16px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 18.754 18.001"
      >
        <defs>
          <clipPath id="clip-path">
            <rect
              id="長方形_172"
              data-name="長方形 172"
              width="3.8"
              height="18"
              transform="translate(-3627 -20930)"
              fill="#fff"
            />
          </clipPath>
        </defs>
        <g
          id="グループ_1015"
          data-name="グループ 1015"
          transform="translate(-812 -5274.524)"
        >
          <path
            id="FontAwsome_star_2"
            data-name="FontAwsome (star)"
            d="M38.293,6l-5.121-.747L30.883.612a1.123,1.123,0,0,0-2.012,0L26.582,5.253,21.46,6a1.122,1.122,0,0,0-.62,1.914l3.705,3.611-.876,5.1A1.121,1.121,0,0,0,25.3,17.806L29.877,15.4l4.582,2.408a1.122,1.122,0,0,0,1.627-1.181l-.876-5.1,3.705-3.611A1.122,1.122,0,0,0,38.293,6ZM33.4,10.935l.831,4.852L29.877,13.5l-4.357,2.289.831-4.852L22.824,7.5,27.7,6.792l2.18-4.417,2.18,4.417L36.93,7.5,33.4,10.935Z"
            transform="translate(791.5 5274.537)"
            fill={fill}
          />
        </g>
      </svg>
    </div>
  )
}

const DecimalStar1: FC<StarIconProps> = ({ size = 'md', color = 'green' }) => {
  const { width, height } = getSizeProps(size)
  const fill = getColorValue(color)

  return (
    <div className="min-w-[16px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 18.754 18.001"
      >
        <defs>
          <clipPath id="clip-path">
            <rect
              id="長方形_172"
              data-name="長方形 172"
              width="3.8"
              height="18"
              transform="translate(-3627 -20930)"
              fill="#fff"
            />
          </clipPath>
        </defs>
        <g
          id="グループ_1015"
          data-name="グループ 1015"
          transform="translate(-812 -5274.524)"
        >
          <g
            id="マスクグループ_3"
            data-name="マスクグループ 3"
            transform="translate(4439 26204.525)"
            clipPath="url(#clip-path)"
          >
            <path
              id="FontAwsome_star_"
              data-name="FontAwsome (star)"
              d="M28.737.6,26.485,5.169l-5.04.735a1.1,1.1,0,0,0-.611,1.883L24.48,11.34l-.862,5.019a1.1,1.1,0,0,0,1.6,1.163l4.509-2.37,4.509,2.37a1.1,1.1,0,0,0,1.6-1.163l-.862-5.019L38.62,7.787A1.1,1.1,0,0,0,38.01,5.9l-5.04-.735L30.717.6A1.1,1.1,0,0,0,28.737.6Z"
              transform="translate(-3647.5 -20929.703)"
              fill={fill}
            />
          </g>
          <path
            id="FontAwsome_star_2"
            data-name="FontAwsome (star)"
            d="M38.293,6l-5.121-.747L30.883.612a1.123,1.123,0,0,0-2.012,0L26.582,5.253,21.46,6a1.122,1.122,0,0,0-.62,1.914l3.705,3.611-.876,5.1A1.121,1.121,0,0,0,25.3,17.806L29.877,15.4l4.582,2.408a1.122,1.122,0,0,0,1.627-1.181l-.876-5.1,3.705-3.611A1.122,1.122,0,0,0,38.293,6ZM33.4,10.935l.831,4.852L29.877,13.5l-4.357,2.289.831-4.852L22.824,7.5,27.7,6.792l2.18-4.417,2.18,4.417L36.93,7.5,33.4,10.935Z"
            transform="translate(791.5 5274.537)"
            fill={fill}
          />
        </g>
      </svg>
    </div>
  )
}

const DecimalStar2: FC<StarIconProps> = ({ size = 'md', color = 'green' }) => {
  const { width, height } = getSizeProps(size)
  const fill = getColorValue(color)

  return (
    <div className="min-w-[16px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 18.754 18.001"
      >
        <defs>
          <clipPath id="clip-path">
            <rect
              id="長方形_171"
              data-name="長方形 171"
              width="7.6"
              height="18"
              transform="translate(-3627 -20907)"
              fill="#fff"
            />
          </clipPath>
        </defs>
        <g
          id="グループ_1014"
          data-name="グループ 1014"
          transform="translate(-812 -5297.524)"
        >
          <g
            id="マスクグループ_4"
            data-name="マスクグループ 4"
            transform="translate(4439 26204.525)"
            clipPath="url(#clip-path)"
          >
            <path
              id="FontAwsome_star_"
              data-name="FontAwsome (star)"
              d="M28.737.6,26.485,5.169l-5.04.735a1.1,1.1,0,0,0-.611,1.883L24.48,11.34l-.862,5.019a1.1,1.1,0,0,0,1.6,1.163l4.509-2.37,4.509,2.37a1.1,1.1,0,0,0,1.6-1.163l-.862-5.019L38.62,7.787A1.1,1.1,0,0,0,38.01,5.9l-5.04-.735L30.717.6A1.1,1.1,0,0,0,28.737.6Z"
              transform="translate(-3647.5 -20906.703)"
              fill={fill}
            />
          </g>
          <path
            id="FontAwsome_star_2"
            data-name="FontAwsome (star)"
            d="M38.293,6l-5.121-.747L30.883.612a1.123,1.123,0,0,0-2.012,0L26.582,5.253,21.46,6a1.122,1.122,0,0,0-.62,1.914l3.705,3.611-.876,5.1A1.121,1.121,0,0,0,25.3,17.806L29.877,15.4l4.582,2.408a1.122,1.122,0,0,0,1.627-1.181l-.876-5.1,3.705-3.611A1.122,1.122,0,0,0,38.293,6ZM33.4,10.935l.831,4.852L29.877,13.5l-4.357,2.289.831-4.852L22.824,7.5,27.7,6.792l2.18-4.417,2.18,4.417L36.93,7.5,33.4,10.935Z"
            transform="translate(791.5 5297.537)"
            fill={fill}
          />
        </g>
      </svg>
    </div>
  )
}

const DecimalStar3: FC<StarIconProps> = ({ size = 'md', color = 'green' }) => {
  const { width, height } = getSizeProps(size)
  const fill = getColorValue(color)

  return (
    <div className="min-w-[16px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 18.754 18.001"
      >
        <defs>
          <clipPath id="clip-path">
            <rect
              id="長方形_170"
              data-name="長方形 170"
              width="11.4"
              height="18"
              transform="translate(-3627 -20880)"
              fill="#fff"
            />
          </clipPath>
        </defs>
        <g
          id="グループ_1016"
          data-name="グループ 1016"
          transform="translate(-812 -5324.524)"
        >
          <g
            id="マスクグループ_5"
            data-name="マスクグループ 5"
            transform="translate(4439 26204.525)"
            clipPath="url(#clip-path)"
          >
            <path
              id="FontAwsome_star_"
              data-name="FontAwsome (star)"
              d="M28.737.6,26.485,5.169l-5.04.735a1.1,1.1,0,0,0-.611,1.883L24.48,11.34l-.862,5.019a1.1,1.1,0,0,0,1.6,1.163l4.509-2.37,4.509,2.37a1.1,1.1,0,0,0,1.6-1.163l-.862-5.019L38.62,7.787A1.1,1.1,0,0,0,38.01,5.9l-5.04-.735L30.717.6A1.1,1.1,0,0,0,28.737.6Z"
              transform="translate(-3647.5 -20879.703)"
              fill={fill}
            />
          </g>
          <path
            id="FontAwsome_star_2"
            data-name="FontAwsome (star)"
            d="M38.293,6l-5.121-.747L30.883.612a1.123,1.123,0,0,0-2.012,0L26.582,5.253,21.46,6a1.122,1.122,0,0,0-.62,1.914l3.705,3.611-.876,5.1A1.121,1.121,0,0,0,25.3,17.806L29.877,15.4l4.582,2.408a1.122,1.122,0,0,0,1.627-1.181l-.876-5.1,3.705-3.611A1.122,1.122,0,0,0,38.293,6ZM33.4,10.935l.831,4.852L29.877,13.5l-4.357,2.289.831-4.852L22.824,7.5,27.7,6.792l2.18-4.417,2.18,4.417L36.93,7.5,33.4,10.935Z"
            transform="translate(791.5 5324.537)"
            fill={fill}
          />
        </g>
      </svg>
    </div>
  )
}

const DecimalStar4: FC<StarIconProps> = ({ size = 'md', color = 'green' }) => {
  const { width, height } = getSizeProps(size)
  const fill = getColorValue(color)

  return (
    <div className="min-w-[16px]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox="0 0 18.754 18.001"
      >
        <defs>
          <clipPath id="clip-path">
            <rect
              id="長方形_169"
              data-name="長方形 169"
              width="15.2"
              height="18"
              transform="translate(-3627 -20849)"
              fill="#fff"
            />
          </clipPath>
        </defs>
        <g
          id="グループ_1017"
          data-name="グループ 1017"
          transform="translate(-812 -5355.524)"
        >
          <g
            id="マスクグループ_6"
            data-name="マスクグループ 6"
            transform="translate(4439 26204.525)"
            clipPath="url(#clip-path)"
          >
            <path
              id="FontAwsome_star_"
              data-name="FontAwsome (star)"
              d="M28.737.6,26.485,5.169l-5.04.735a1.1,1.1,0,0,0-.611,1.883L24.48,11.34l-.862,5.019a1.1,1.1,0,0,0,1.6,1.163l4.509-2.37,4.509,2.37a1.1,1.1,0,0,0,1.6-1.163l-.862-5.019L38.62,7.787A1.1,1.1,0,0,0,38.01,5.9l-5.04-.735L30.717.6A1.1,1.1,0,0,0,28.737.6Z"
              transform="translate(-3647.5 -20848.703)"
              fill={fill}
            />
          </g>
          <path
            id="FontAwsome_star_2"
            data-name="FontAwsome (star)"
            d="M38.293,6l-5.121-.747L30.883.612a1.123,1.123,0,0,0-2.012,0L26.582,5.253,21.46,6a1.122,1.122,0,0,0-.62,1.914l3.705,3.611-.876,5.1A1.121,1.121,0,0,0,25.3,17.806L29.877,15.4l4.582,2.408a1.122,1.122,0,0,0,1.627-1.181l-.876-5.1,3.705-3.611A1.122,1.122,0,0,0,38.293,6ZM33.4,10.935l.831,4.852L29.877,13.5l-4.357,2.289.831-4.852L22.824,7.5,27.7,6.792l2.18-4.417,2.18,4.417L36.93,7.5,33.4,10.935Z"
            transform="translate(791.5 5355.537)"
            fill={fill}
          />
        </g>
      </svg>
    </div>
  )
}

export const EvaluationStarIcon: FC<EvaluationStarIconProps> = ({
  size = 'md',
  evaluation,
  color = 'green'
}) => {
  if (evaluation === null) {
    return (
      <div className="flex justify-start opacity-25">
        {[...Array(5)].map((_, index) => (
          <div key={`star-evaluation-${String(index)}`} className="mr-1">
            <StarIcon size={size} color={color} />
          </div>
        ))}
      </div>
    )
  }

  const integer = Math.floor(evaluation)
  const decimal = evaluation - integer

  let decimalStarElem = <StarIcon size={size} color={color} />
  if (decimal === 0.0) {
    decimalStarElem = <EmptyStarIcon size={size} color={color} />
  } else if (decimal < 0.2) {
    decimalStarElem = <DecimalStar1 size={size} color={color} />
  } else if (decimal < 0.4) {
    decimalStarElem = <DecimalStar2 size={size} color={color} />
  } else if (decimal < 0.6) {
    decimalStarElem = <DecimalStar3 size={size} color={color} />
  } else if (decimal < 0.8) {
    decimalStarElem = <DecimalStar4 size={size} color={color} />
  }

  // 白色版の場合は、小数点を表示せず整数のみ表示（元のEvalurationStarIconWhiteの動作を踏襲）
  if (color === 'white') {
    return (
      <div className="flex justify-start">
        {[...Array(integer)].map((_, index) => (
          <div key={`star-evaluation-white-${String(index)}`} className="mr-1">
            <StarIcon size={size} color="white" />
          </div>
        ))}
        {integer < 5 && (
          <>
            {[...Array(5 - integer)].map((_, index) => (
              <div key={`empty-white-${String(index)}`} className="mr-1">
                <EmptyStarIcon size={size} color="white" />
              </div>
            ))}
          </>
        )}
      </div>
    )
  }

  // 緑色版の場合は、小数点も表示（元のEvalurationStarIconの動作を踏襲）
  return (
    <div className="flex justify-start">
      {[...Array(integer)].map((_, index) => (
        <div key={`star-evaluation-${String(index)}`} className="mr-1">
          <StarIcon size={size} color={color} />
        </div>
      ))}
      {integer < 5 && (
        <>
          <div className="mr-1">{decimalStarElem}</div>
          {[...Array(4 - integer)].map((_, index) => (
            <div key={`empty-${String(index)}`} className="mr-1">
              <EmptyStarIcon size={size} color={color} />
            </div>
          ))}
        </>
      )}
    </div>
  )
}